import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Row,
  UncontrolledDropdown,
  UncontrolledTooltip,
} from "reactstrap";
import AppConstant from "../../../constants/app.constants";

//Import Breadcrumb
import Breadcrumbs from "components/Common/BreadcrumbTitle";

import { decryptedValue, isPermission } from "common/utility";
import { getSystemConfiguration } from "http/UAM/systemConfig.helper";
import TableContainerV1 from '../../../components/Common/TableContainerV1';
import { useToast } from "ToastStore";
import ToastDialog from "components/Common/ToastDialog";

const TableActionsComponent = (rowData) => {
  const navigate = useNavigate();

  const user = decryptedValue(localStorage.getItem("user"));

  const navigateToeEditSystemConfiguration = (id) => {
    const encryptedId = btoa(id.toString());
    navigate(`/${user.roleName}/system-config/edit?id=${encodeURIComponent(encryptedId)}`)
  };
  
  const navigateToeViewSystemConfiguration = (id) => {
    const encryptedId = btoa(id.toString());
    navigate(`/${user.roleName}/system-config/view?id=${encodeURIComponent(encryptedId)}`)
  };

  return (
    <UncontrolledDropdown>
      <DropdownToggle tag="a" className="card-drop">
        <i className="mdi mdi-dots-horizontal font-size-18"></i>
      </DropdownToggle>
      <DropdownMenu className="dropdown-menu-end">
        <DropdownItem
          onClick={() => {
            navigateToeViewSystemConfiguration(rowData.id);
          }
          }
        >
          <i className="mdi mdi-eye font-size-16 text-success me-1" id="edittooltip"></i>
          View
          <UncontrolledTooltip placement="top" target="edittooltip">
            View
          </UncontrolledTooltip>
        </DropdownItem>
        <DropdownItem
          onClick={() => {
            navigateToeEditSystemConfiguration(rowData.id);
          }
          }
        >
          <i className="mdi mdi-pencil font-size-16 text-success me-1" id="edittooltip"></i>
          Edit
          <UncontrolledTooltip placement="top" target="edittooltip">
            Edit
          </UncontrolledTooltip>
        </DropdownItem>
      </DropdownMenu>
    </UncontrolledDropdown>
  )
}


const SystemConfiguration = props => {
  document.title = "System Configuration | FundLync";

  const navigate = useNavigate();

  const [systemConfigList, setSystemConfigList] = useState([]);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageCount: 0, totalRecords: 0 });
  const [pageSize, setPageSize] = useState(10);
  const [searchValue, setSearchValue] = useState("");
  const [columnId, setColumnId] = useState("id");
  const [sortingOrderStatus, setSortingOrderStatus] = useState("desc");
  const [isNewPermission, setIsNewPermission] = useState(false)
  const [sortingOptions, setSortingOptions] = useState({ column: 'id', isAsc: false, optionalColumn: ['Action'] })

  const { message, showToast, role, handleHideToast } = useToast()
  const user = decryptedValue(localStorage.getItem("user"));  // Customber Column
  const columns = useMemo(
    () => [
      {
        Header: 'Id',
        accessor: 'id',
        filterable: true,
        Cell: (cellProps) => {
          return cellProps.value
            ;
        }
      },
      {
        Header: 'Config Key',
        accessor: 'configKey',
        filterable: true,
        Cell: (cellProps) => {
          return cellProps.value;
        }
      },
      {
        Header: 'Description',
        accessor: 'configDescription',
        filterable: true,
        Cell: (cellProps) => {
          return cellProps.value;
        }
      },
      {
        Header: 'Action',
        Cell: (cellProps) => {
          const rowData = cellProps.row.original;
          return TableActionsComponent(rowData);
        }
      },
    ],
    []
  );


  useEffect(() => {
    getSystemConfiguration({ page: 1, limit: AppConstant.TABLE_RECORD_SIZE })
      .then(responseFromAPI => {
        if (responseFromAPI.status && responseFromAPI.code == 200) {
          setSystemConfigList(responseFromAPI.data.data);
          const { totalItems, totalPages, currentPage } = responseFromAPI.data
          setPagination({ pageIndex: currentPage, pageCount: totalPages, totalRecords: totalItems });
        }
      }).catch(error => { })
    checkPermissions()
  }, []);

  const checkPermissions = async () => {

    const isAddNew = await isPermission("system_configuration", "create_permission")
    setIsNewPermission(isAddNew)
  }

  const handleCustomerClicks = () => {
    navigate(`/${user.roleName}/system-config/add`)
  };

  const handlePageChange = (page, pageSize, searchKey = searchValue, columnName = columnId, sortingOrder = sortingOrderStatus) => {
    setPageSize(pageSize);
    getSystemConfiguration({ page, limit: pageSize, searchKey, columnName, sortingOrder })
      .then(responseFromAPI => {
        if (responseFromAPI.status && responseFromAPI.code == 200) {
          setSystemConfigList(responseFromAPI.data.data);
          const { totalItems, totalPages, currentPage } = responseFromAPI.data
          setPagination({ pageIndex: currentPage, pageCount: totalPages, totalRecords: totalItems });
        }else {
          setPagination({ pageIndex: 0, pageCount: 0, totalRecords: 0 })
          setSystemConfigList([])
        }
      }).catch(error => { })
  }

  const handleSortColumn = (option) => {
    setSortingOptions(option)
    const columnName = option.column;
    setColumnId(columnName)
    const sortingOrder = option.isAsc ? "asc" : "desc";
    setSortingOrderStatus(sortingOrder)
    handlePageChange(1, pageSize, searchValue, columnName, sortingOrder)
  }

  const handleSearchFilter = (value, pageSize) => {
    setSearchValue(value)
    handlePageChange(1, pageSize, value);
  }
  const handleClearSearch = () => {
    setSearchValue("")
    handlePageChange(1, pageSize, "", "id", "desc")
  }

  const buttonSection = (
    <div className="text-sm-end">
      {isNewPermission &&
        <Button
          type="button"
          color="success"
          className="btn-rounded mb-2 me-2"
          onClick={handleCustomerClicks}
        >
          <i className="mdi mdi-plus me-1" />
          {'New System Configuration'}
        </Button>
      }
    </div>);


  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="SYSTEM CONFIGURATIONS" />
        <Row className="vertical-scroll">
          <Col xs="12">
            <Card>
              <CardBody>
                <TableContainerV1
                  columns={columns}
                  data={systemConfigList}
                  pagination={pagination}
                  handlePageChange={handlePageChange}
                  handleSearchFilter={handleSearchFilter}
                  customSection={buttonSection}
                  sortOptions={sortingOptions}
                  handleSortColumn={handleSortColumn}
                  handleClearSearch={handleClearSearch}
                  className="custom-header-css"
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
      <ToastDialog
        message={message}
        showToast={showToast}
        role={role}
        onCloseFn={handleHideToast}
      />
    </div>
  );
};

export default SystemConfiguration